import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { useSuche } from '../../hooks/useQualyz'

const TREFFER_MAX = 8

/**
 * Suche im Kopf nach Kuerzel, Name oder ISIN (Kim, 14.09.2026). Treffer
 * oeffnen die Einzelansicht. Fuer alle sichtbar -- was man danach von einer
 * Aktie sieht, entscheidet die Einzelansicht.
 */
export default function Suche() {
  const [eingabe, setEingabe] = useState('')
  const [begriff, setBegriff] = useState('')
  const [offen, setOffen] = useState(false)
  const [aktiv, setAktiv] = useState(0)
  const navigate = useNavigate()
  const box = useRef<HTMLDivElement>(null)
  const treffer = useSuche(begriff)
  const liste = (treffer.data ?? []).slice(0, TREFFER_MAX)

  // Erst nach einer kurzen Pause fragen -- nicht bei jedem Tastendruck.
  useEffect(() => {
    const warten = window.setTimeout(() => setBegriff(eingabe), 200)
    return () => window.clearTimeout(warten)
  }, [eingabe])

  useEffect(() => setAktiv(0), [begriff])

  useEffect(() => {
    const schliessen = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOffen(false)
    }
    document.addEventListener('mousedown', schliessen)
    return () => document.removeEventListener('mousedown', schliessen)
  }, [])

  const oeffnen = (symbol: string) => {
    setEingabe('')
    setBegriff('')
    setOffen(false)
    navigate(`/aktie/${encodeURIComponent(symbol)}`)
  }

  const taste = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOffen(true)
      setAktiv((a) => Math.min(a + 1, Math.max(liste.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setAktiv((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      const ziel = liste[aktiv]
      if (ziel) {
        e.preventDefault()
        oeffnen(ziel.symbol)
      }
    } else if (e.key === 'Escape') {
      setOffen(false)
    }
  }

  const zeigen = offen && eingabe.trim().length > 0
  const wartet = treffer.isFetching || begriff !== eingabe

  return (
    <div className="q-suche" role="search" ref={box}>
      <Search aria-hidden="true" />
      <input
        type="search"
        value={eingabe}
        placeholder="Ticker, Name oder ISIN"
        aria-label="Aktie suchen"
        aria-expanded={zeigen}
        aria-controls="q-suche-treffer"
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          setEingabe(e.target.value)
          setOffen(true)
        }}
        onFocus={() => setOffen(true)}
        onKeyDown={taste}
      />
      {zeigen && (
        <div className="q-suche-treffer" id="q-suche-treffer" role="listbox" aria-label="Treffer">
          {liste.length === 0 ? (
            <div className="q-suche-leer">{wartet ? 'Suche …' : 'Kein Titel gefunden.'}</div>
          ) : (
            liste.map((t, i) => (
              <button
                key={t.symbol}
                type="button"
                role="option"
                aria-selected={i === aktiv}
                className={i === aktiv ? 'is-aktiv' : undefined}
                onMouseEnter={() => setAktiv(i)}
                onClick={() => oeffnen(t.symbol)}
              >
                <span className="q-sym">{t.symbol}</span>
                <span className="q-suche-name">{t.name}</span>
                <small>{t.isin ?? t.boerse ?? ''}</small>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
